// src/pages/cadastros/Defeitos/DefeitosPorMaquina.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

function DefeitosPorMaquina() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [listaSetores, setListaSetores] = useState([]);
  const [listaMaquinas, setListaMaquinas] = useState([]);

  const [empresaId, setEmpresaId] = useState("");
  const [setorId, setSetorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");

  const [defeitos, setDefeitos] = useState([]);

  const [filtroEsquerda, setFiltroEsquerda] = useState("");
  const [filtroDireita, setFiltroDireita] = useState("");

  const [selecionadosEsquerda, setSelecionadosEsquerda] = useState([]);
  const [selecionadosDireita, setSelecionadosDireita] = useState([]);

  const [novoDefeitoNome, setNovoDefeitoNome] = useState("");

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [mobileTab, setMobileTab] = useState("disponiveis");

  useEffect(() => {
    async function carregarBase() {
      if (!usuarioLogado?.grupoId) {
        setErro(
          "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
        );
        setCarregando(false);
        return;
      }

      try {
        const grupoId = usuarioLogado.grupoId;

        // Empresas
        const empQ = query(
          collection(db, "empresas"),
          where("grupoId", "==", usuarioLogado.grupoId)
        );
        const snapEmp = await getDocs(empQ);
        const listaEmp = snapEmp.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => e.grupoId === grupoId);
        setListaEmpresas(listaEmp);

        // Setores
        const setQ = query(
          collection(db, "setores"),
          where("grupoId", "==", grupoId)
        );
        const snapSet = await getDocs(setQ);
        const listaSet = snapSet.docs.map((d) => ({ id: d.id, ...d.data() }));
        setListaSetores(listaSet);

        // Máquinas
        const maqQ = query(
          collection(db, "maquinas"),
          where("grupoId", "==", usuarioLogado.grupoId)
        );
        const snapMaq = await getDocs(maqQ);
        const listaMaq = snapMaq.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((m) => m.grupoId === grupoId);
        setListaMaquinas(listaMaq);

        // Defeitos (todos do grupo)
        const defRef = collection(db, "defeitos");
        const qDef = query(defRef, where("grupoId", "==", grupoId));
        const snapDef = await getDocs(qDef);
        const listaDef = snapDef.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setDefeitos(listaDef);
      } catch (e) {
        console.error("Erro ao carregar base de defeitos por máquina:", e);
        setErro("Erro ao carregar dados. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarBase();
  }, [usuarioLogado]);

  const setoresFiltrados = useMemo(() => {
    if (!empresaId) return listaSetores;
    return listaSetores.filter((s) => s.empresaId === empresaId);
  }, [listaSetores, empresaId]);

  const maquinasFiltradas = useMemo(() => {
    let base = [...listaMaquinas];
    if (empresaId) base = base.filter((m) => m.empresaId === empresaId);
    if (setorId) base = base.filter((m) => m.setorId === setorId);
    return base.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  }, [listaMaquinas, empresaId, setorId]);

  const maquinaSelecionada = useMemo(
    () => listaMaquinas.find((m) => m.id === maquinaId) || null,
    [listaMaquinas, maquinaId]
  );

  // Separa defeitos da máquina x e disponíveis
  const { defeitosDisponiveis, defeitosDaMaquina } = useMemo(() => {
    if (!maquinaId) {
      return { defeitosDisponiveis: [], defeitosDaMaquina: [] };
    }

    const daMaquina = [];
    const disponiveis = [];

    defeitos.forEach((d) => {
      const arr = d.maquinasIds || [];
      if (arr.includes(maquinaId)) {
        daMaquina.push(d);
      } else {
        disponiveis.push(d);
      }
    });

    const filtroE = filtroEsquerda.trim().toLowerCase();
    const filtroD = filtroDireita.trim().toLowerCase();

    const disponiveisFiltrados = disponiveis
      .filter((d) =>
        !filtroE ? true : String(d.nome || "").toLowerCase().includes(filtroE)
      )
      .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));

    const daMaquinaFiltrados = daMaquina
      .filter((d) =>
        !filtroD ? true : String(d.nome || "").toLowerCase().includes(filtroD)
      )
      .sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));

    return {
      defeitosDisponiveis: disponiveisFiltrados,
      defeitosDaMaquina: daMaquinaFiltrados,
    };
  }, [defeitos, maquinaId, filtroEsquerda, filtroDireita]);

  function resetSelecoes() {
    setSelecionadosEsquerda([]);
    setSelecionadosDireita([]);
  }

  async function adicionarMaquinaA(defeitosIds) {
    if (!maquinaId || defeitosIds.length === 0) return;
    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const maquina = maquinaSelecionada;
      const nomeMaquina = maquina?.nome || "(sem nome)";

      const updates = defeitosIds.map(async (id) => {
        const def = defeitos.find((d) => d.id === id);
        if (!def) return;

        const atuaisIds = def.maquinasIds || [];
        const atuaisNomes = def.maquinasNomes || [];

        if (atuaisIds.includes(maquinaId)) return;

        const novoIds = [...atuaisIds, maquinaId];
        const novoNomes = [...atuaisNomes, nomeMaquina];

        await updateDoc(doc(db, "defeitos", id), {
          grupoId: usuarioLogado?.grupoId || null,
          grupoNome: usuarioLogado?.grupoNome || usuarioLogado?.grupo || null,
          maquinasIds: novoIds,
          maquinasNomes: novoNomes,
          updatedAt: new Date(),
        });
      });

      await Promise.all(updates);

      // Atualiza estado local
      const novos = defeitos.map((d) => {
        if (!defeitosIds.includes(d.id)) return d;
        const atuaisIds = d.maquinasIds || [];
        const atuaisNomes = d.maquinasNomes || [];
        if (atuaisIds.includes(maquinaId)) return d;

        return {
          ...d,
          maquinasIds: [...atuaisIds, maquinaId],
          maquinasNomes: [...atuaisNomes, maquinaSelecionada?.nome || "(sem nome)"],
        };
      });
      setDefeitos(novos);
      setSucesso("Defeitos vinculados à máquina com sucesso.");
      resetSelecoes();
    } catch (e) {
      console.error("Erro ao vincular defeitos à máquina:", e);
      setErro("Erro ao vincular defeitos à máquina.");
    } finally {
      setSalvando(false);
    }
  }

  async function removerMaquinaDe(defeitosIds) {
    if (!maquinaId || defeitosIds.length === 0) return;
    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const updates = defeitosIds.map(async (id) => {
        const def = defeitos.find((d) => d.id === id);
        if (!def) return;

        const atuaisIds = def.maquinasIds || [];
        const atuaisNomes = def.maquinasNomes || [];

        const idx = atuaisIds.indexOf(maquinaId);
        if (idx === -1) return;

        const novoIds = [...atuaisIds];
        const novoNomes = [...atuaisNomes];
        novoIds.splice(idx, 1);
        novoNomes.splice(idx, 1);

        await updateDoc(doc(db, "defeitos", id), {
          grupoId: usuarioLogado?.grupoId || null,
          grupoNome: usuarioLogado?.grupoNome || usuarioLogado?.grupo || null,
          maquinasIds: novoIds,
          maquinasNomes: novoNomes,
          updatedAt: new Date(),
        });
      });

      await Promise.all(updates);

      const novos = defeitos.map((d) => {
        if (!defeitosIds.includes(d.id)) return d;

        const atuaisIds = d.maquinasIds || [];
        const atuaisNomes = d.maquinasNomes || [];
        const idx = atuaisIds.indexOf(maquinaId);
        if (idx === -1) return d;

        const novoIds = [...atuaisIds];
        const novoNomes = [...atuaisNomes];
        novoIds.splice(idx, 1);
        novoNomes.splice(idx, 1);

        return {
          ...d,
          maquinasIds: novoIds,
          maquinasNomes: novoNomes,
        };
      });

      setDefeitos(novos);
      setSucesso("Defeitos removidos desta máquina.");
      resetSelecoes();
    } catch (e) {
      console.error("Erro ao remover defeitos da máquina:", e);
      setErro("Erro ao remover defeitos da máquina.");
    } finally {
      setSalvando(false);
    }
  }

  async function handleCriarNovoDefeito() {
    if (!novoDefeitoNome.trim()) return;

    if (!usuarioLogado?.grupoId || !empresaId || !maquinaId) {
      setErro("Selecione empresa, setor e máquina antes de criar um defeito.");
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const grupoId = usuarioLogado.grupoId;
      const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;

      const empresaSelecionada = listaEmpresas.find((e) => e.id === empresaId) || null;
      const empresaNome = empresaSelecionada?.nome || null;

      const setorSelecionado =
        setoresFiltrados.find((s) => s.id === setorId) || null;
      const setorNome = setorSelecionado?.nome || null;

      const maquina = maquinaSelecionada;
      const maquinaNome = maquina?.nome || "(sem nome)";

      const novaRef = doc(collection(db, "defeitos"));

      const novo = {
        nome: novoDefeitoNome.trim(),
        observacoes: null,

        grupoId,
        grupoNome,

        empresaId,
        empresaNome,
        globalEmpresa: false,

        setorId,
        setorNome,
        todosSetoresEmpresa: false,

        maquinasIds: [maquinaId],
        maquinasNomes: [maquinaNome],

        ativo: true,
        criadoPor: usuarioLogado?.uid || null,
        criadoPorNome: usuarioLogado?.nome || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(novaRef, novo);

      setDefeitos((prev) => [...prev, { id: novaRef.id, ...novo }]);
      setNovoDefeitoNome("");
      setSucesso("Defeito criado e vinculado à máquina.");
    } catch (e) {
      console.error("Erro ao criar defeito:", e);
      setErro("Erro ao criar defeito.");
    } finally {
      setSalvando(false);
    }
  }

  function voltarLista() {
    navigate("/cadastros/defeitos");
  }

  if (isMobile) {
    return (
      <LayoutMobile
          tema={tema}
          alterarTema={alterarTema}
          titulo="Defeitos por Máquina"
          sidebarAberta={sidebarAberta}
          onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        >
        <div className="m-form-container">
          <div className="m-form-header">
            <h2 className="m-form-title">Vincular Defeitos</h2>
            <p className="m-form-subtitle">Selecione empresa, setor e máquina</p>
          </div>

          {erro && <div className="m-error-banner">{erro}</div>}
          {sucesso && <div className="m-success-banner">{sucesso}</div>}

          {carregando ? (
            <div className="m-loading-state">
              <span className="m-loading-spinner"></span>
              <p>Carregando...</p>
            </div>
          ) : (
            <>
              <div className="m-form-group">
                <label className="m-form-label">Empresa</label>
                <select
                  className="m-form-select"
                  value={empresaId}
                  onChange={(e) => {
                    setEmpresaId(e.target.value);
                    setSetorId("");
                    setMaquinaId("");
                    resetSelecoes();
                  }}
                >
                  <option value="">Selecione</option>
                  {listaEmpresas.map((e) => (
                    <option key={e.id} value={e.id}>{e.nome || "(sem nome)"}</option>
                  ))}
                </select>
              </div>

              <div className="m-form-group">
                <label className="m-form-label">Setor</label>
                <select
                  className="m-form-select"
                  value={setorId}
                  onChange={(e) => {
                    setSetorId(e.target.value);
                    setMaquinaId("");
                    resetSelecoes();
                  }}
                  disabled={!empresaId}
                >
                  {!empresaId ? (
                    <option value="">Selecione a empresa</option>
                  ) : (
                    <>
                      <option value="">Todos os setores</option>
                      {setoresFiltrados.map((s) => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div className="m-form-group">
                <label className="m-form-label">Máquina</label>
                <select
                  className="m-form-select"
                  value={maquinaId}
                  onChange={(e) => {
                    setMaquinaId(e.target.value);
                    resetSelecoes();
                  }}
                  disabled={!empresaId}
                >
                  {!empresaId ? (
                    <option value="">Selecione empresa / setor</option>
                  ) : (
                    <>
                      <option value="">Selecione a máquina</option>
                      {maquinasFiltradas.map((m) => (
                        <option key={m.id} value={m.id}>{m.nome}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {maquinaId && (
                <>
                  <div className="m-tabs-container">
                    <button
                      type="button"
                      className={`m-tab-btn ${mobileTab === "disponiveis" ? "active" : ""}`}
                      onClick={() => setMobileTab("disponiveis")}
                    >
                      Disponíveis ({defeitosDisponiveis.length})
                    </button>
                    <button
                      type="button"
                      className={`m-tab-btn ${mobileTab === "vinculados" ? "active" : ""}`}
                      onClick={() => setMobileTab("vinculados")}
                    >
                      Vinculados ({defeitosDaMaquina.length})
                    </button>
                  </div>

                  {mobileTab === "disponiveis" && (
                    <div className="m-defeitos-list">
                      <div className="m-list-header">
                        <input
                          type="text"
                          className="m-form-input"
                          placeholder="Filtrar defeitos..."
                          value={filtroEsquerda}
                          onChange={(e) => setFiltroEsquerda(e.target.value)}
                        />
                      </div>

                      <div className="m-checkbox-list">
                        {defeitosDisponiveis.length === 0 ? (
                          <p className="m-empty-hint">Nenhum defeito disponível</p>
                        ) : (
                          defeitosDisponiveis.map((d) => (
                            <label key={d.id} className="m-checkbox-item">
                              <input
                                type="checkbox"
                                checked={selecionadosEsquerda.includes(d.id)}
                                onChange={() => setSelecionadosEsquerda((prev) =>
                                  prev.includes(d.id)
                                    ? prev.filter((x) => x !== d.id)
                                    : [...prev, d.id]
                                )}
                              />
                              <span className="m-checkbox-mark"></span>
                              <span className="m-checkbox-text">{d.nome}</span>
                            </label>
                          ))
                        )}
                      </div>

                      {selecionadosEsquerda.length > 0 && (
                        <button
                          type="button"
                          className="m-btn-pill m-btn-primary m-btn-full"
                          onClick={() => adicionarMaquinaA(selecionadosEsquerda)}
                          disabled={salvando}
                        >
                          {salvando ? "Vinculando..." : `Vincular ${selecionadosEsquerda.length} defeito(s)`}
                        </button>
                      )}

                      <div className="m-new-defeito-section">
                        <p className="m-section-label">Criar novo defeito</p>
                        <div className="m-inline-form">
                          <input
                            type="text"
                            className="m-form-input"
                            placeholder="Nome do defeito..."
                            value={novoDefeitoNome}
                            onChange={(e) => setNovoDefeitoNome(e.target.value)}
                          />
                          <button
                            type="button"
                            className="m-btn-pill m-btn-primary"
                            onClick={handleCriarNovoDefeito}
                            disabled={salvando || !novoDefeitoNome.trim()}
                          >
                            + Criar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {mobileTab === "vinculados" && (
                    <div className="m-defeitos-list">
                      <div className="m-list-header">
                        <input
                          type="text"
                          className="m-form-input"
                          placeholder="Filtrar defeitos..."
                          value={filtroDireita}
                          onChange={(e) => setFiltroDireita(e.target.value)}
                        />
                      </div>

                      <div className="m-checkbox-list">
                        {defeitosDaMaquina.length === 0 ? (
                          <p className="m-empty-hint">Nenhum defeito vinculado</p>
                        ) : (
                          defeitosDaMaquina.map((d) => (
                            <label key={d.id} className="m-checkbox-item">
                              <input
                                type="checkbox"
                                checked={selecionadosDireita.includes(d.id)}
                                onChange={() => setSelecionadosDireita((prev) =>
                                  prev.includes(d.id)
                                    ? prev.filter((x) => x !== d.id)
                                    : [...prev, d.id]
                                )}
                              />
                              <span className="m-checkbox-mark"></span>
                              <span className="m-checkbox-text">{d.nome}</span>
                            </label>
                          ))
                        )}
                      </div>

                      {selecionadosDireita.length > 0 && (
                        <button
                          type="button"
                          className="m-btn-pill m-btn-secondary m-btn-full"
                          onClick={() => removerMaquinaDe(selecionadosDireita)}
                          disabled={salvando}
                        >
                          {salvando ? "Removendo..." : `Remover ${selecionadosDireita.length} defeito(s)`}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="m-form-actions">
                <button
                  type="button"
                  className="m-btn-pill m-btn-secondary"
                  onClick={voltarLista}
                >
                  Voltar
                </button>
              </div>
            </>
          )}
        </div>
      </LayoutMobile>
    );
  }

  if (carregando) {
    return (
      <LayoutDesktop titulo="Defeitos por máquina">
        <section style={{ width: "100%" }}>
          <div className="pc-card">
            <p>Carregando dados...</p>
          </div>
        </section>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Defeitos por Máquina"
      subtitulo=""
    >
      <section style={{ width: "100%" }}>
        <div className="pc-card">
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0 }}>Defeitos por máquina</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                Selecione empresa, setor e máquina e organize os defeitos usando as duas listas.
              </p>
            </div>
            <button className="pc-btn-secundario" type="button" onClick={voltarLista}>
              Voltar
            </button>
          </header>

          {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
          {sucesso && (
            <div className="pc-alert pc-alert-sucesso">{sucesso}</div>
          )}

          <div className="pc-form-row">
            <div className="pc-form-group">
              <label>Empresa</label>
              <select
                value={empresaId}
                onChange={(e) => {
                  setEmpresaId(e.target.value);
                  setSetorId("");
                  setMaquinaId("");
                  resetSelecoes();
                }}
              >
                <option value="">Selecione</option>
                {listaEmpresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </div>

            <div className="pc-form-group">
              <label>Setor</label>
              <select
                value={setorId}
                onChange={(e) => {
                  setSetorId(e.target.value);
                  setMaquinaId("");
                  resetSelecoes();
                }}
                disabled={!empresaId}
              >
                {!empresaId ? (
                  <option value="">Selecione a empresa primeiro</option>
                ) : (
                  <>
                    <option value="">Todos os setores</option>
                    {setoresFiltrados.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome || "(sem nome)"}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className="pc-form-group">
              <label>Máquina</label>
              <select
                value={maquinaId}
                onChange={(e) => {
                  setMaquinaId(e.target.value);
                  resetSelecoes();
                }}
                disabled={!empresaId}
              >
                {!empresaId ? (
                  <option value="">Selecione empresa / setor</option>
                ) : (
                  <>
                    <option value="">Selecione a máquina</option>
                    {maquinasFiltradas.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nome || "(sem nome)"}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          {!maquinaId ? (
            <p style={{ marginTop: 16 }}>
              Selecione empresa, setor e máquina para organizar os defeitos.
            </p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  gap: 16,
                  marginTop: 16,
                  alignItems: "stretch",
                }}
              >
                {/* Lista esquerda */}
                <div className="pc-card" style={{ padding: 12 }}>
                  <div style={{ marginBottom: 10 }}>
                    <strong style={{ fontSize: 14 }}>Defeitos disponíveis</strong>
                    <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
                      (clique para selecionar, duplo clique para adicionar)
                    </span>
                    <input
                      type="text"
                      placeholder="Filtrar defeitos..."
                      value={filtroEsquerda}
                      onChange={(e) => setFiltroEsquerda(e.target.value)}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--cor-borda-input, #334155)",
                      borderRadius: 6,
                      height: 280,
                      overflow: "auto",
                      background: "var(--cor-input-bg, transparent)",
                    }}
                  >
                    {defeitosDisponiveis.map((d) => {
                      const selecionado = selecionadosEsquerda.includes(d.id);
                      return (
                        <div
                          key={d.id}
                          onClick={() =>
                            setSelecionadosEsquerda((prev) =>
                              prev.includes(d.id)
                                ? prev.filter((x) => x !== d.id)
                                : [...prev, d.id]
                            )
                          }
                          onDoubleClick={() => adicionarMaquinaA([d.id])}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            background: selecionado
                              ? "var(--cor-acento, #2563eb)"
                              : "transparent",
                            color: selecionado ? "#fff" : "var(--cor-input-texto, #1e293b)",
                            borderBottom: "1px solid var(--cor-borda-input, rgba(255,255,255,0.1))",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            border: selecionado ? "2px solid #fff" : "2px solid var(--cor-borda-input, #666)",
                            background: selecionado ? "#fff" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            color: selecionado ? "var(--cor-acento, #2563eb)" : "transparent",
                            flexShrink: 0,
                          }}>
                            {selecionado && "✓"}
                          </span>
                          <span style={{ flex: 1 }}>{d.nome || "(sem nome)"}</span>
                        </div>
                      );
                    })}
                    {defeitosDisponiveis.length === 0 && (
                      <p style={{ padding: 16, opacity: 0.6, textAlign: "center" }}>
                        Nenhum defeito disponível.
                      </p>
                    )}
                  </div>

                  <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      placeholder="Criar novo defeito..."
                      value={novoDefeitoNome}
                      onChange={(e) => setNovoDefeitoNome(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleCriarNovoDefeito()}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="pc-btn-primario"
                      onClick={handleCriarNovoDefeito}
                      disabled={salvando || !novoDefeitoNome.trim()}
                      title="Criar novo defeito"
                    >
                      + Criar
                    </button>
                  </div>
                </div>

                {/* Botões centrais */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    padding: "0 8px",
                  }}
                >
                  <button
                    type="button"
                    className="pc-btn-primario"
                    onClick={() => adicionarMaquinaA(selecionadosEsquerda)}
                    disabled={salvando || selecionadosEsquerda.length === 0}
                    title="Adicionar selecionados à máquina"
                    style={{ 
                      minWidth: 48, 
                      padding: "10px 14px",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    ▶
                  </button>
                  <button
                    type="button"
                    className="pc-btn-primario"
                    onClick={() =>
                      adicionarMaquinaA(defeitosDisponiveis.map((d) => d.id))
                    }
                    disabled={salvando || defeitosDisponiveis.length === 0}
                    title="Adicionar todos à máquina"
                    style={{ 
                      minWidth: 48, 
                      padding: "10px 14px",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    ▶▶
                  </button>
                  <div style={{ height: 16 }} />
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => removerMaquinaDe(selecionadosDireita)}
                    disabled={salvando || selecionadosDireita.length === 0}
                    title="Remover selecionados da máquina"
                    style={{ 
                      minWidth: 48, 
                      padding: "10px 14px",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() =>
                      removerMaquinaDe(defeitosDaMaquina.map((d) => d.id))
                    }
                    disabled={salvando || defeitosDaMaquina.length === 0}
                    title="Remover todos da máquina"
                    style={{ 
                      minWidth: 48, 
                      padding: "10px 14px",
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    ◀◀
                  </button>
                </div>

                {/* Lista direita */}
                <div className="pc-card" style={{ padding: 12 }}>
                  <div style={{ marginBottom: 10 }}>
                    <strong style={{ fontSize: 14 }}>Defeitos da máquina</strong>
                    <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
                      (duplo clique para remover)
                    </span>
                    <input
                      type="text"
                      placeholder="Filtrar defeitos..."
                      value={filtroDireita}
                      onChange={(e) => setFiltroDireita(e.target.value)}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </div>
                  <div
                    style={{
                      border: "1px solid var(--cor-borda-input, #334155)",
                      borderRadius: 6,
                      height: 280,
                      overflow: "auto",
                      background: "var(--cor-input-bg, transparent)",
                    }}
                  >
                    {defeitosDaMaquina.map((d) => {
                      const selecionado = selecionadosDireita.includes(d.id);
                      return (
                        <div
                          key={d.id}
                          onClick={() =>
                            setSelecionadosDireita((prev) =>
                              prev.includes(d.id)
                                ? prev.filter((x) => x !== d.id)
                                : [...prev, d.id]
                            )
                          }
                          onDoubleClick={() => removerMaquinaDe([d.id])}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            background: selecionado
                              ? "var(--cor-acento, #2563eb)"
                              : "transparent",
                            color: selecionado ? "#fff" : "var(--cor-input-texto, #1e293b)",
                            borderBottom: "1px solid var(--cor-borda-input, rgba(255,255,255,0.1))",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            border: selecionado ? "2px solid #fff" : "2px solid var(--cor-borda-input, #666)",
                            background: selecionado ? "#fff" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            color: selecionado ? "var(--cor-acento, #2563eb)" : "transparent",
                            flexShrink: 0,
                          }}>
                            {selecionado && "✓"}
                          </span>
                          <span style={{ flex: 1 }}>{d.nome || "(sem nome)"}</span>
                        </div>
                      );
                    })}
                    {defeitosDaMaquina.length === 0 && (
                      <p style={{ padding: 16, opacity: 0.6, textAlign: "center" }}>
                        Nenhum defeito associado a esta máquina.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default DefeitosPorMaquina;
